import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, Plus, Trash2, ArrowLeft, Save, LayoutTemplate, Code2, Wand2 } from 'lucide-react';
import { btnPrimary, btnGhost, inputCls } from '../ChatAssistant/ui';
import { mailApi, fmtDate, STARTER_HTML, previewHtml } from './api';
import { HtmlEditor } from './Editor';
import EmailStudio from './studio/EmailStudio';

function TemplateEditor({ brandId, initial, onBack, onSaved }) {
  const [t, setT] = useState(initial);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(t) !== JSON.stringify(initial);

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: t.name, subject: t.subject, preheader: t.preheader, html: t.html };
      const r = t.id
        ? await mailApi(brandId, `/templates/${t.id}`, { method: 'PATCH', body })
        : await mailApi(brandId, '/templates', { method: 'POST', body });
      toast.success('Template saved');
      onSaved(r.template);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button className={btnGhost} onClick={() => (!dirty || window.confirm('Discard unsaved changes?')) && onBack()}><ArrowLeft />Templates</button>
        <button className={btnPrimary} onClick={save} disabled={busy || !t.name?.trim() || !dirty}>{busy ? <Loader2 className="animate-spin" /> : <Save />}Save</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Template name</label><input className={inputCls} value={t.name || ''} onChange={(e) => setT({ ...t, name: e.target.value })} /></div>
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Default subject</label><input className={inputCls} value={t.subject || ''} onChange={(e) => setT({ ...t, subject: e.target.value })} /></div>
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Default preview text</label><input className={inputCls} value={t.preheader || ''} onChange={(e) => setT({ ...t, preheader: e.target.value })} /></div>
      </div>
      <HtmlEditor value={t.html || ''} onChange={(html) => setT({ ...t, html })} />
    </div>
  );
}

export default function TemplatesTab({ brandId }) {
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null);
  const [studio, setStudio] = useState(null); // { initial, lastTheme }

  const load = useCallback(async () => {
    try { setTemplates((await mailApi(brandId, '/templates')).templates); }
    catch (e) { toast.error(e.message); setTemplates([]); }
  }, [brandId]);
  useEffect(() => { setTemplates(null); setEditing(null); setStudio(null); load(); }, [load]);

  // Stüdyo şablonları stüdyoda, elle yazılan HTML şablonları kod editöründe açılır
  const open = async (t) => {
    try {
      const full = (await mailApi(brandId, `/templates/${t.id}`)).template;
      if (full.design) setStudio({ initial: full });
      else setEditing(full);
    } catch (e) { toast.error(e.message); }
  };
  // Yeni tasarım: markanın en son stüdyo şablonunun teması (logo, renkler) boş başlangıca taşınır
  const newDesign = async () => {
    let lastTheme = null;
    const last = (templates || []).find((t) => t.studio);
    if (last) {
      try { lastTheme = (await mailApi(brandId, `/templates/${last.id}`)).template.design?.theme || null; } catch (e) { /* temasız başla */ }
    }
    setStudio({ initial: {}, lastTheme });
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Campaigns made from it are not affected.`)) return;
    try { await mailApi(brandId, `/templates/${t.id}`, { method: 'DELETE' }); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (studio) {
    return <EmailStudio brandId={brandId} initial={studio.initial} lastTheme={studio.lastTheme}
      onClose={() => { setStudio(null); load(); }} onSaved={() => {}} />;
  }

  if (editing) {
    return <TemplateEditor brandId={brandId} initial={editing} onBack={() => { setEditing(null); load(); }} onSaved={(t) => setEditing(t)} />;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-gray-900">Templates</h2>
          <p className="text-xs text-gray-500 mt-0.5">Reusable email designs. A campaign copies the template, so editing a template later doesn't change sent campaigns.</p>
        </div>
        <div className="flex gap-2">
          <button className={btnGhost} onClick={() => setEditing({ name: 'Untitled template', subject: '', preheader: '', html: STARTER_HTML })}><Code2 />Paste HTML</button>
          <button className={btnPrimary} onClick={newDesign}><Plus />New design</button>
        </div>
      </div>

      {templates === null ? (
        <div className="flex items-center justify-center py-16 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 py-12 text-center">
          <LayoutTemplate className="w-6 h-6 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-700 font-medium">No templates yet</p>
          <p className="text-xs text-gray-500 mt-1">Design one in the studio or paste HTML from your designer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-indigo-300 cursor-pointer" onClick={() => open(t)}>
              <div className="h-48 overflow-hidden bg-gray-100 relative pointer-events-none">
                <iframe title={t.name} sandbox="" srcDoc={previewHtml(t.html)} className="border-0 bg-white origin-top-left" style={{ width: 760, height: 760, transform: 'scale(0.42)' }} />
              </div>
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-gray-100">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{t.studio && <Wand2 className="w-3 h-3 inline mr-1 text-indigo-500 -mt-0.5" />}{t.name}</div>
                  <div className="text-[11px] text-gray-500 truncate">{t.subject || 'No subject'} · {fmtDate(t.updatedAt)}</div>
                </div>
                <button className="text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); remove(t); }} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
